import getApiInstance from '../common/apiInstance';
import { getApiErrorMessage } from '../common/apiErrorMessage';
import {
  DEFAULT_BOARD_THEME,
  isBoardThemeId,
  type BoardThemeId,
} from 'react-chess-core';
import {
  DEFAULT_REPLAY_AUTOPLAY_SPEED,
  normalizeReplayAutoplaySpeed,
  type ReplayAutoplaySpeed,
} from './replayAutoplaySpeed';
import {
  DEFAULT_COURSE_SRS_REPETITIONS,
  normalizeCourseSrsRepetitions,
  type CourseSrsRepetitions,
} from './courseSrsRepetitions';
import {
  normalizeBlackRepertoireForks,
  normalizeWhiteRepertoireForks,
  type BlackRepertoireForks,
  type WhiteRepertoireForks,
} from 'endchess-contracts';
import {
  DEFAULT_TUTORIAL_SPLASH_STATUSES,
  normalizeTutorialSplashes,
  type TutorialSplashStatuses,
} from './tutorialSplashStatuses';

const settingsApi = getApiInstance('/settings', 'no-settings');

export type UserSettings = {
  addMissedPuzzlesToSrs: boolean;
  addMissedPositionsToSrs: boolean;
  autoShowReplayWrongMoves: boolean;
  autoShowTopReplayGame: boolean;
  autoTopUpImports: boolean;
  boardTheme: BoardThemeId;
  replayAutoplaySpeed: ReplayAutoplaySpeed;
  courseSrsRepetitions: CourseSrsRepetitions;
  whiteRepertoireForks?: WhiteRepertoireForks;
  blackRepertoireForks?: BlackRepertoireForks;
  tutorialSplashes: TutorialSplashStatuses;
};

export type SettingChangeSource =
  | 'settings_page'
  | 'replay_hub'
  | 'replay_autoplay'
  | 'course_trainer';

export const DEFAULT_SETTINGS: UserSettings = {
  addMissedPuzzlesToSrs: true,
  addMissedPositionsToSrs: true,
  autoShowReplayWrongMoves: true,
  autoShowTopReplayGame: true,
  autoTopUpImports: false,
  boardTheme: DEFAULT_BOARD_THEME,
  replayAutoplaySpeed: DEFAULT_REPLAY_AUTOPLAY_SPEED,
  courseSrsRepetitions: DEFAULT_COURSE_SRS_REPETITIONS,
  tutorialSplashes: DEFAULT_TUTORIAL_SPLASH_STATUSES,
};

const normalizeBoardTheme = (value: unknown): BoardThemeId =>
  isBoardThemeId(value) ? value : DEFAULT_BOARD_THEME;

const normalize = (data: Partial<UserSettings> | undefined): UserSettings => ({
  addMissedPuzzlesToSrs: data?.addMissedPuzzlesToSrs !== false,
  addMissedPositionsToSrs: data?.addMissedPositionsToSrs !== false,
  autoShowReplayWrongMoves: data?.autoShowReplayWrongMoves !== false,
  autoShowTopReplayGame: data?.autoShowTopReplayGame !== false,
  autoTopUpImports: data?.autoTopUpImports === true,
  boardTheme: normalizeBoardTheme(data?.boardTheme),
  replayAutoplaySpeed: normalizeReplayAutoplaySpeed(data?.replayAutoplaySpeed),
  courseSrsRepetitions: normalizeCourseSrsRepetitions(data?.courseSrsRepetitions),
  whiteRepertoireForks: normalizeWhiteRepertoireForks(data?.whiteRepertoireForks),
  blackRepertoireForks: normalizeBlackRepertoireForks(data?.blackRepertoireForks),
  tutorialSplashes: normalizeTutorialSplashes(data?.tutorialSplashes),
});

let inflightFetch: Promise<UserSettings> | null = null;

const fetchSettingsOnce = async (): Promise<UserSettings> => {
  try {
    const { data, status } = await settingsApi.get<UserSettings>('/', {
      skipGlobalErrorToast: true,
      skipAuthRedirect: true,
      validateStatus: (s) => s === 200 || s === 401,
    });
    if (status === 401) {
      return DEFAULT_SETTINGS;
    }
    return normalize(data);
  } catch {
    return DEFAULT_SETTINGS;
  }
};

// GET /settings — returns defaults when signed out or on failure.
export const fetchSettings = async (): Promise<UserSettings> => {
  if (!inflightFetch) {
    inflightFetch = fetchSettingsOnce().finally(() => {
      inflightFetch = null;
    });
  }
  return inflightFetch;
};

// PUT /settings — persists a partial patch and returns the saved settings.
export const updateSettings = async (
  patch: Partial<UserSettings>,
  options?: { source?: SettingChangeSource },
): Promise<UserSettings> => {
  try {
    const { data } = await settingsApi.put<UserSettings>('/', {
      ...patch,
      ...(options?.source ? { changeSource: options.source } : {}),
    });
    return normalize(data);
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, { action: 'Failed to save settings' }),
    );
  }
};
